<template>
  <nav class="pagination" aria-label="pagination">
    <span id="prefix" hidden>Page</span>
    <component
      :is="current > 1 ? 'RouterLink' : 'div'"
      id="link-first"
      :to="current > 1 ? getUrl(current - 1) : null"
      aria-labelledby="link-first prefix"
      class="pagination__action pagination__action--prev"
    >
      <span>Previous</span>

      <SvgSprite
        class="icon"
        symbol="ui-arrow-left"
        size="12"
        aria-hidden="true"
      />
    </component>

    <ul class="pagination__list">
      <!-- First page -->
      <li v-if="current > siblings + 1" class="pagination__item">
        <RouterLink :to="getUrl(1)" class="pagination__item__link">
          <span>1</span>
        </RouterLink>
      </li>
      <!-- ...-->
      <li v-if="current > siblings + 2" class="pagination__item">…</li>

      <!-- Previous pages -->
      <template v-for="item in siblings" :key="`previous-${item}`">
        <li v-if="current + (item - siblings - 1) > 0" class="pagination__item">
          <RouterLink
            :id="`link-${current + (item - siblings - 1)}`"
            :to="getUrl(current + (item - siblings - 1))"
            :aria-labelledby="`prefix link-${current + (item - siblings - 1)}`"
            class="pagination__item__link"
          >
            <span>{{ current + (item - siblings - 1) }}</span>
          </RouterLink>
        </li>
      </template>

      <!-- Current page -->
      <li class="pagination__item">
        <RouterLink
          :id="`link-${current}`"
          :to="getUrl(current)"
          class="pagination__item__link active"
          :aria-labelledby="`prefix link-${current}`"
          aria-current="true"
        >
          <span>{{ current }}</span>
        </RouterLink>
      </li>

      <!-- Next pages -->
      <template v-for="item in siblings" :key="`next-${item}`">
        <li v-if="current + item < total" class="pagination__item">
          <RouterLink
            :id="`link-${current + item}`"
            :to="getUrl(current + item)"
            :aria-labelledby="`prefix link-${current + item}`"
            class="pagination__item__link"
          >
            <span>{{ current + item }}</span>
          </RouterLink>
        </li>
      </template>

      <li v-if="current < total - (siblings + 1)" class="pagination__item">
        …
      </li>
      <!-- Last page  -->
      <li v-if="current < total" class="pagination__item">
        <RouterLink :to="getUrl(total)" class="pagination__item__link">
          <span>{{ total }}</span>
        </RouterLink>
      </li>
    </ul>

    <component
      :is="current < total ? 'RouterLink' : 'div'"
      id="link-last"
      :to="current < total ? getUrl(current + 1) : null"
      aria-labelledby="link-last prefix"
      class="pagination__action"
    >
      <span>Next</span>

      <SvgSprite
        class="icon"
        symbol="ui-arrow-right"
        size="12"
        aria-hidden="true"
      />
    </component>
  </nav>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'

// Props
const props = defineProps({
  total: {
    type: Number,
    required: true,
  },
  current: {
    type: Number,
    required: false,
    default: 1,
  },
  useQueryParam: {
    type: Boolean,
    required: false,
    default: false,
  },
  origin: {
    type: String,
    required: false,
    default: null,
  },
})

// Vars
const route = useRoute()
const siblings = 1 // Amount of next and previous pages to show
const baseUrl = route.path

const getUrl = (number: number): string => {
  if (props.origin && !props.origin.includes('undefined')) {
    // if (Object.keys(route.query).length) {
    //   return `${baseUrl}&page=${number}`
    // }

    return `${props.origin}page/${number}/`
  }

  return baseUrl
}
</script>

<style lang="scss" scoped>
$item-spacing: #{$spacing * 0.25};

.pagination {
  position: relative;
  width: fit-content;
}

.pagination__action {
  /* stylelint-disable-next-line declaration-no-important */
  --padding-v: #{$spacing * 0.3} !important;

  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  padding: 0.8rem;
  color: $c-black;
  opacity: 0.3;
  transition: 0.2s $ease-out;
  cursor: not-allowed;

  span {
    @extend %visually-hidden;
  }

  .icon {
    width: 1.2rem;
    height: 1.2rem;
    transition: transform 0.1s $ease-out;
  }

  &:last-child {
    right: 0;
    left: initial;
  }

  &[href] {
    opacity: 1;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      .icon {
        transform: translateX(-0.2rem);
      }

      &:last-child .icon {
        transform: translateX(0.2rem);
      }
    }
  }
}

.pagination__list {
  @extend %list-nostyle;

  margin: 0 calc(3.6rem + #{$item-spacing});
  color: $c-grey-dark;
}

.pagination__item {
  display: inline-block;

  & + & {
    margin-left: $item-spacing;
  }
}

.pagination__item__link {
  @extend %fw-medium;
  @extend %text-nodecoration;

  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-inline: 1.4rem;
  height: 3rem;
  color: rgba($c-black, 30%);
  font-size: 1.6rem;
  background: $c-bg-btn;
  border: 1px solid $c-bg-btn;
  border-radius: 0.8rem;
  transition: color 0.2s;

  span {
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    z-index: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: $c-white;
    border-radius: 0.8rem;
    opacity: 0;
    transform: scale(0.5);
    transition:
      opacity 0.2s,
      transform 0.2s;
  }

  &:hover,
  &:active,
  &:focus-visible {
    color: $c-black;

    &::after {
      opacity: 0.5;
      transform: scale(1);
    }
  }

  &.active,
  &.active:hover,
  &.active:focus-visible {
    color: $c-black;

    &::after {
      opacity: 1;
      transform: scale(1);
    }
  }
}
</style>
